import { useState, useCallback } from 'react';
import axios from 'axios';
import { parseServerErrors } from '../utils/validationUtils';
import { parseAmountShorthand } from '../utils/amountUtils';
import { settleDateAdjustment } from '../utils/settlement';

export type CanonicalTicketStatus =
  | 'DRAFT'
  | 'PRECLEAR_PENDING'
  | 'PRECLEAR_APPROVED'
  | 'PRECLEAR_REJECTED'
  | 'SENT'
  | 'EXECUTED'
  | 'POSTED'
  | 'CANCELLED'
  | 'VOIDED';

export interface PreClearComplianceBreach {
  message: string;
  ruleId?: number;
  ruleKey?: string;
  ruleName?: string;
  severity?: string;
  currentValue?: number;
  limitValue?: number;
  /** How to print the two values: '%' (default), 'factor', 'bps', 'years', 'USD' or '' (no number). */
  unit?: string;
}

export interface PreClearBlock {
  breaches: PreClearComplianceBreach[];
  commitmentExposure: number;
  fundedPar: number;
  unfundedCommitment: number;
  cashImpact: number;
}

export const normalizeTicketStatus = (status: string | null | undefined): CanonicalTicketStatus | null => {
  if (!status) return null;
  const normalized = status.toUpperCase();
  switch (normalized) {
    case 'PENDING_PRECLEAR':
      return 'PRECLEAR_PENDING';
    case 'APPROVED':
      return 'PRECLEAR_APPROVED';
    case 'REJECTED':
      return 'PRECLEAR_REJECTED';
    case 'DRAFT':
    case 'PRECLEAR_PENDING':
    case 'PRECLEAR_APPROVED':
    case 'PRECLEAR_REJECTED':
    case 'SENT':
    case 'EXECUTED':
    case 'POSTED':
    case 'CANCELLED':
    case 'VOIDED':
      return normalized;
    default:
      return null;
  }
};

// Per-ticket lifecycle overrides (e.g. an LL multi-fund BLOCK_PARENT must route
// send/execute to POST /api/credit/ll/blocks/{id}/advance — the generic
// /api/trade-transactions endpoints reject block parents server-side). An
// override returns true when it handled the action; returning false falls
// through to the generic endpoint. Errors thrown by an override surface via
// the hook's standard parseServerErrors path.
export interface TicketWorkflowOverrideHelpers {
  setTransactionStatus: (status: CanonicalTicketStatus | null) => void;
  setErrors: (errors: { [key: string]: string }) => void;
  notifySuccess: (message: string) => void;
}

export interface TicketWorkflowOverrides {
  // R3-2 (LL-FR-14): pre-clear overrides so a block parent can fan
  // submit/approve out to every ALLOCATION_CHILD — pre-clearing only the
  // parent leaves the children DRAFT and bypasses per-portfolio compliance.
  submitPreClear?: (transactionId: number, helpers: TicketWorkflowOverrideHelpers) => Promise<boolean>;
  approvePreClear?: (transactionId: number, notes: string | undefined, helpers: TicketWorkflowOverrideHelpers) => Promise<boolean>;
  send?: (transactionId: number, helpers: TicketWorkflowOverrideHelpers) => Promise<boolean>;
  execute?: (transactionId: number, executionData: any, helpers: TicketWorkflowOverrideHelpers) => Promise<boolean>;
  // R4-6c: save/update override. transactionId is the editing id (null on
  // create). Lets a ticket reroute the save of an LL BLOCK_PARENT to
  // POST /api/credit/ll/blocks/{id}/allocate — the generic
  // PUT /api/trade-transactions/{id} is rejected server-side for block rows.
  saveDraft?: (transactionId: number | null, transactionData: any, helpers: TicketWorkflowOverrideHelpers) => Promise<boolean>;
}

export interface TicketWorkflowState {
  isLoading: boolean;
  errors: { [key: string]: string };
  transactionStatus: CanonicalTicketStatus | null;
  editingTransactionId: number | null;
  isEditMode: boolean;
  showSuccess: boolean;
  successMessage: string;
  preClearBlock: PreClearBlock | null;
  overrideRequestPending: boolean;
}

export interface TicketWorkflowActions {
  setErrors: (errors: { [key: string]: string } | ((prev: { [key: string]: string }) => { [key: string]: string })) => void;
  setTransactionStatus: (status: CanonicalTicketStatus | null) => void;
  setEditingTransactionId: (id: number | null) => void;
  setIsEditMode: (mode: boolean) => void;
  setShowSuccess: (show: boolean) => void;
  setSuccessMessage: (message: string) => void;
  /** Saves the draft and returns the trade as the server stored it (null when nothing was saved). */
  handleDraft: (transactionData: any) => Promise<any>;
  handleSubmitForPreClear: (transactionId: number) => Promise<void>;
  handleRequestComplianceOverride: (transactionId: number, justification: string) => Promise<boolean>;
  clearPreClearBlock: () => void;
  setOverrideRequestPending: (pending: boolean) => void;
  handleApprovePreClear: (transactionId: number, notes?: string) => Promise<void>;
  handleRejectPreClear: (transactionId: number, rejectionReason: string) => Promise<void>;
  handleSend: (transactionId: number) => Promise<void>;
  handleExecute: (transactionId: number, executionData: any) => Promise<void>;
  handleVoid: (transactionId: number) => Promise<void>;
  handleReject: (transactionId: number) => Promise<void>;
  handleDuplicate: (transactionData: any) => Promise<void>;
}

export const useTicketWorkflow = (overrides?: TicketWorkflowOverrides): [TicketWorkflowState, TicketWorkflowActions] => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrorsState] = useState<{ [key: string]: string }>({});
  
  const setErrors = useCallback((errors: { [key: string]: string } | ((prev: { [key: string]: string }) => { [key: string]: string })) => {
    if (typeof errors === 'function') {
      setErrorsState(errors);
    } else {
      setErrorsState(errors);
    }
  }, []);
  const [transactionStatus, setTransactionStatus] = useState<CanonicalTicketStatus | null>(null);
  const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [preClearBlock, setPreClearBlock] = useState<PreClearBlock | null>(null);
  const [overrideRequestPending, setOverrideRequestPending] = useState(false);

  const notifySuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 5000);
  }, []);

  const handleDraft = useCallback(async (transactionData: any) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      // R4-6c: per-ticket save override (e.g. LL BLOCK_PARENT allocation via
      // POST /api/credit/ll/blocks/{id}/allocate). Returning true means the
      // override fully handled the save — never fall through to the generic
      // endpoint. Errors it throws surface via parseServerErrors below.
      if (overrides?.saveDraft
          && await overrides.saveDraft(editingTransactionId, transactionData, { setTransactionStatus, setErrors, notifySuccess })) {
        return null;
      }
      const url = editingTransactionId
        ? `/api/trade-transactions/${editingTransactionId}`
        : '/api/trade-transactions';
      
      const method = editingTransactionId ? 'put' : 'post';
      
      console.log('handleDraft - Sending transaction data:', {
        portfolioId: transactionData.portfolioId,
        allocations: transactionData.allocations,
        allocationsCount: transactionData.allocations?.length || 0,
        hasAllocations: !!transactionData.allocations && transactionData.allocations.length > 0
      });
      
      const response = await axios[method](url, transactionData);
      
      if (response.data) {
        setEditingTransactionId(response.data.id);
        setTransactionStatus('DRAFT');
        setIsEditMode(true);
        // The server rolls a settle date that falls on a holiday forward (ADR 0009 §6); say so
        // rather than leave the ticket showing a date the book does not hold (F-BD-006).
        const moved = settleDateAdjustment(transactionData?.settleDate, response.data.settleDate);
        setSuccessMessage(`Transaction ${isEditMode ? 'updated' : 'created'} successfully!${moved ? ` ${moved}` : ''}`);
        setShowSuccess(true);
        
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage('');
        }, moved ? 8000 : 3000);
      }
      return response.data || null;
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [editingTransactionId, isEditMode, overrides, notifySuccess, setErrors]);

  const handleSend = useCallback(async (transactionId: number) => {
    setIsLoading(true);
    setErrors({});

    try {
      if (overrides?.send
          && await overrides.send(transactionId, { setTransactionStatus, setErrors, notifySuccess })) {
        return;
      }
      await axios.post(`/api/trade-transactions/${transactionId}/send`);
      setTransactionStatus('SENT');
      setSuccessMessage('Trade released successfully!');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, [overrides, notifySuccess, setErrors]);

  const handleExecute = useCallback(async (transactionId: number, executionData: any) => {
    setIsLoading(true);
    setErrors({});

    try {
      if (overrides?.execute
          && await overrides.execute(transactionId, executionData, { setTransactionStatus, setErrors, notifySuccess })) {
        return;
      }
      await axios.post(`/api/trade-transactions/${transactionId}/execute`, executionData);
      setTransactionStatus('EXECUTED');
      setSuccessMessage('Transaction executed successfully!');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, [overrides, notifySuccess, setErrors]);

  const handleVoid = useCallback(async (transactionId: number) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      await axios.post(`/api/trade-transactions/${transactionId}/void`);
      setTransactionStatus('CANCELLED');
      setSuccessMessage('Transaction voided successfully!');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleReject = useCallback(async (transactionId: number) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      await axios.post(`/api/trade-transactions/${transactionId}/reject`);
      setTransactionStatus('CANCELLED');
      setSuccessMessage('Transaction rejected successfully!');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSubmitForPreClear = useCallback(async (transactionId: number) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      if (overrides?.submitPreClear
          && await overrides.submitPreClear(transactionId, { setTransactionStatus, setErrors, notifySuccess })) {
        return;
      }
      const response = await axios.post(`/api/pre-clear/${transactionId}/submit`);
      setPreClearBlock(null);
      setOverrideRequestPending(false);
      setTransactionStatus(normalizeTicketStatus(response.data.status) || 'PRECLEAR_PENDING');
      setSuccessMessage('Transaction submitted for pre-clear review!');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const responseErrors = Array.isArray(error.response?.data?.errors)
        ? error.response.data.errors
        : [];
      const overrideErrors = responseErrors.filter((item: any) =>
        String(item?.field || '').startsWith('complianceOverride')
        && item?.rejectedValue?.overrideAvailable === true
      );
      if (overrideErrors.length > 0) {
        const firstContext = overrideErrors[0].rejectedValue || {};
        setPreClearBlock({
          breaches: overrideErrors.map((item: any) => ({
            message: item.message,
            ruleId: item.rejectedValue?.ruleId,
            ruleKey: item.rejectedValue?.ruleKey,
            ruleName: item.rejectedValue?.ruleName,
            severity: item.rejectedValue?.severity,
            currentValue: item.rejectedValue?.currentValue,
            limitValue: item.rejectedValue?.limitValue,
            unit: item.rejectedValue?.unit,
          })),
          commitmentExposure: Number(firstContext.commitmentExposure || 0),
          fundedPar: Number(firstContext.fundedPar || 0),
          unfundedCommitment: Number(firstContext.unfundedCommitment || 0),
          cashImpact: Number(firstContext.cashImpact || 0),
        });
      } else {
        const serverErrors = parseServerErrors(error);
        setErrors(serverErrors);
      }
    } finally {
      setIsLoading(false);
    }
  }, [overrides, notifySuccess, setErrors]);

  const handleRequestComplianceOverride = useCallback(async (
    transactionId: number,
    justification: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setErrors({});
    try {
      const response = await axios.post(
        `/api/pre-clear/${transactionId}/request-override`,
        { justification }
      );
      setTransactionStatus(normalizeTicketStatus(response.data.status) || 'PRECLEAR_PENDING');
      setOverrideRequestPending(true);
      setPreClearBlock(null);
      notifySuccess('Override request submitted to the CCO review queue.');
      return true;
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      if (Object.keys(serverErrors).length === 0) {
        serverErrors.submit = 'Unable to submit the override request. Please try again.';
      }
      setErrors(serverErrors);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [notifySuccess, setErrors]);

  const clearPreClearBlock = useCallback(() => setPreClearBlock(null), []);

  const handleApprovePreClear = useCallback(async (transactionId: number, notes?: string) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      if (overrides?.approvePreClear
          && await overrides.approvePreClear(transactionId, notes, { setTransactionStatus, setErrors, notifySuccess })) {
        return;
      }
      const response = await axios.post(`/api/pre-clear/${transactionId}/approve`, null, {
        params: notes ? { notes } : {}
      });
      setTransactionStatus(normalizeTicketStatus(response.data.status) || 'PRECLEAR_APPROVED');
      setSuccessMessage('Transaction approved for pre-clear!');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, [overrides, notifySuccess, setErrors]);

  const handleRejectPreClear = useCallback(async (transactionId: number, rejectionReason: string) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await axios.post(`/api/pre-clear/${transactionId}/reject`, null, {
        params: { rejectionReason }
      });
      setTransactionStatus(normalizeTicketStatus(response.data.status) || 'PRECLEAR_REJECTED');
      setSuccessMessage('Transaction rejected in pre-clear review.');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDuplicate = useCallback(async (_transactionData: any) => {
    // Duplicate = an editable, UNSAVED copy of the loaded ticket — no server
    // call. The previous implementation re-sent raw form data through
    // handleDraft, which PUT it at the loaded trade's id: for POSTED trades
    // the backend refused (posted economics are immutable) and the click
    // looked dead; for drafts it overwrote the original instead of copying.
    // The screen keeps its form contents; clearing the id/status turns the
    // next Save Draft into a POST that creates the brand-new trade.
    setEditingTransactionId(null);
    setIsEditMode(false);
    setTransactionStatus(null);
    setErrors({});
    setSuccessMessage('Ticket duplicated — review and Save Draft to create the copy');
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 4000);
  }, [setErrors]);

  const state: TicketWorkflowState = {
    isLoading,
    errors,
    transactionStatus,
    editingTransactionId,
    isEditMode,
    showSuccess,
    successMessage,
    preClearBlock,
    overrideRequestPending
  };

  const actions: TicketWorkflowActions = {
    setErrors,
    setTransactionStatus,
    setEditingTransactionId,
    setIsEditMode,
    setShowSuccess,
    setSuccessMessage,
    handleDraft,
    handleSubmitForPreClear,
    handleRequestComplianceOverride,
    clearPreClearBlock,
    setOverrideRequestPending,
    handleApprovePreClear,
    handleRejectPreClear,
    handleSend,
    handleExecute,
    handleVoid,
    handleReject,
    handleDuplicate
  };

  return [state, actions];
};
